import { useEffect, useRef, useState } from "react";
import { Button } from "./Button";

/**
 * The overflow menu docs/UI_ACTIONS.md rule 6 depends on (#327).
 *
 * "Four visible actions, then a menu" was unenforceable without one --
 * there was no menu component in the app at all, which is a large part
 * of why button rows grew instead: adding one more was the only move
 * available.
 *
 * Deliberately small. This is a list of actions, not a general popover:
 * no submenus, no icons, no keyboard-shortcut column. Anything wanting
 * those is a Modal.
 *
 * ACCESSIBILITY, because a menu built from divs is a menu a keyboard
 * user cannot reach:
 *   - the trigger is a real button with aria-haspopup and aria-expanded
 *   - items are real buttons in a list, so Tab reaches them in order
 *   - Escape closes and returns focus to the trigger, which is where the
 *     user was
 *   - a click anywhere else closes it
 */
export function ActionMenu({ label, items = [], disabled = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function onDocClick(e) {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        // Back where they were, not at the top of the document.
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const usable = items.filter(Boolean);
  if (usable.length === 0) return null;

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <Button
        ref={triggerRef}
        variant="secondary"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label} ▾
      </Button>

      {open && (
        <ul
          role="menu"
          aria-label={label}
          style={{
            position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 20,
            listStyle: "none", margin: 0, padding: "var(--space-1)",
            minWidth: "14rem",
            background: "var(--color-bg-elevated)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md)",
            boxShadow: "0 6px 20px rgb(0 0 0 / 18%)",
          }}
        >
          {usable.map((item) => (
            <li key={item.label} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
                className="touch-target"
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: "none", border: "none", font: "inherit",
                  padding: "var(--space-2)", borderRadius: "var(--radius-sm)",
                  color: item.danger ? "var(--color-danger)" : "var(--color-text)",
                  cursor: item.disabled ? "not-allowed" : "pointer",
                  opacity: item.disabled ? 0.5 : 1,
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
